import React, { memo, useMemo } from "react";
import { View, Image } from "react-native";
import { SvgUri } from "react-native-svg";

const SvgImage = ({
  source,
  uri,
  width,
  height,
  aspectRatio,
  preserveAspectRatio = "xMidYMid meet",
  style,
}) => {
  const renderInfo = useMemo(() => {
    if (typeof uri === "string" && uri.length > 0) {
      return { kind: "uri", uri };
    }

    const maybeComponent = source?.default ?? source;
    if (typeof maybeComponent === "function") {
      return { kind: "component", Component: maybeComponent };
    }

    const resolvedUri =
      typeof source === "string"
        ? source
        : Image.resolveAssetSource?.(source)?.uri;

    if (typeof resolvedUri === "string" && resolvedUri.length > 0) {
      return { kind: "uri", uri: resolvedUri };
    }

    return { kind: "none" };
  }, [source, uri]);

  const effectiveAspectRatio =
    typeof aspectRatio === "number" && Number.isFinite(aspectRatio)
      ? aspectRatio
      : null;

  const containerStyle = useMemo(() => {
    const baseStyle = {
      flexShrink: 0,
      overflow: "hidden",
    };

    if (width != null && height != null) {
      return [baseStyle, { width, height }, style];
    }

    if (width != null) {
      return [
        baseStyle,
        {
          width,
          ...(effectiveAspectRatio != null
            ? { aspectRatio: effectiveAspectRatio }
            : {}),
        },
        style,
      ];
    }

    if (height != null) {
      return [
        baseStyle,
        {
          height,
          ...(effectiveAspectRatio != null
            ? { aspectRatio: effectiveAspectRatio }
            : {}),
        },
        style,
      ];
    }

    return [baseStyle, style];
  }, [width, height, style, effectiveAspectRatio]);

  const svgSizingProps = useMemo(() => {
    const canFillContainer =
      (width != null && height != null) ||
      (effectiveAspectRatio != null && (width != null || height != null));

    return {
      preserveAspectRatio,
      ...(canFillContainer
        ? { width: "100%", height: "100%" }
        : {
            ...(width != null ? { width } : {}),
            ...(height != null ? { height } : {}),
          }),
    };
  }, [preserveAspectRatio, width, height, effectiveAspectRatio]);

  return (
    <View style={containerStyle}>
      {renderInfo.kind === "component" ? (
        <renderInfo.Component {...svgSizingProps} />
      ) : renderInfo.kind === "uri" ? (
        <SvgUri uri={renderInfo.uri} {...svgSizingProps} />
      ) : null}
    </View>
  );
};

export default memo(SvgImage);
